import {
  closest,
  contains,
  createElement,
  focusNextChild,
  focusPreviousChild,
  getAttributes,
  replaceWith,
} from "../utils";

import Disclosure from "./Disclosure";

/**
 * A navigation menu component with disclosure item sub-menus.
 *
 * The component follows the "Disclosure Navigation Menu" example from the
 * WAI ARIA Authoring Practices Guide. See
 * {@link https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/examples/disclosure-navigation/}.
 *
 * Any descendant `<details>` elements will be replaced with
 * `<ifrs-disclosure haspopup>`.
 *
 * @example
 * ```html
 * <ifrs-navigation-menu>
 *   <ul>
 *     <li>
 *       <details>
 *         <summary>Menu Item 1</summary>
 *         <ul>
 *           // ...
 *         </ul>
 *       </details>
 *     <li>
 *   </ul>
 * <ifrs-navigation-menu>
 * ```
 */
export default class NavigationMenu extends HTMLElement {
  /**
   * Define the element in the document's custom element registry.
   *
   * @param {string} [tag] The tag to use in the element definition
   */
  static define(tag = "ifrs-navigation-menu") {
    if (!customElements.get(tag)) {
      customElements.define(tag, this);
    }
  }

  /**
   * A collection for side effect cleanup functions. Each function in the set
   * is called and removed when the component is disconnected.
   *
   * @type {Set<() => void>}
   */
  cleanup = new Set();

  /**
   * Set up the element once it has been added to the DOM.
   */
  connectedCallback() {
    // Exit early if the component is not connected
    if (!this.isConnected) {
      return;
    }

    // Set up component markup
    this.setup();

    // Run setup again when children change
    const observer = new MutationObserver(() => this.setup());
    observer.observe(this, { childList: true });
    this.cleanup.add(() => observer.disconnect());

    /**
     * When a descendant disclosure element opens, close all other
     * disclosure elements.
     *
     * @param {Event} event A toggle event.
     */
    const handleToggle = (event) => {
      if (event.target instanceof Disclosure) {
        const disclosure = event.target;
        if (disclosure.open) {
          this.querySelectorAll("ifrs-disclosure").forEach((el) => {
            if (el instanceof Disclosure && el !== disclosure) {
              el.open = false;
            }
          });
        }
      }
    };

    // Add toggle event handlers.
    this.addEventListener("toggle", handleToggle);
    this.cleanup.add(() => this.removeEventListener("toggle", handleToggle));

    /**
     *
     * @param {KeyboardEvent} event A keyboard event
     */
    const handleKeyDown = (event) => {
      if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
        event.preventDefault();
        focusPreviousChild(closest(event.target, "ul"));
      }
      if (event.key === "ArrowDown" || event.key === "ArrowRight") {
        event.preventDefault();
        focusNextChild(closest(event.target, "ul"));
      }
    };

    this.addEventListener("keydown", handleKeyDown);
    this.cleanup.add(() => this.removeEventListener("keydown", handleKeyDown));

    let mouseoverTimeout;

    /**
     *
     * @param {MouseEvent} event A mouse event.
     */
    const handleMouseEnter = (event) => {
      if (event.currentTarget instanceof Disclosure) {
        clearTimeout(mouseoverTimeout);
        event.currentTarget.open = true;
      }
    };

    /**
     *
     * @param {MouseEvent} event A mouse event.
     */
    const handleMouseLeave = (event) => {
      const el = event.currentTarget;
      if (el instanceof Disclosure) {
        mouseoverTimeout = setTimeout(() => {
          if (el && !contains(el, document.activeElement)) {
            el.open = false;
          }
        }, 300);
      }
    };

    /**
     * @param {FocusEvent} event A focus event
     */
    const handleFocusOut = (event) => {
      const el = event.currentTarget;
      if (el instanceof Disclosure && !contains(el, event.relatedTarget)) {
        el.open = false;
      }
    };

    this.querySelectorAll("ifrs-disclosure").forEach((el) => {
      el.addEventListener("focusout", handleFocusOut);
      this.cleanup.add(() => el.removeEventListener("focusout", handleFocusOut));

      if (matchMedia("(hover: hover)").matches) {
        el.addEventListener("mouseenter", handleMouseEnter);
        el.addEventListener("mouseleave", handleMouseLeave);
        this.cleanup.add(() => {
          el.removeEventListener("mouseenter", handleMouseEnter);
          el.removeEventListener("mouseleave", handleMouseLeave);
        });
      }
    });
  }

  /**
   * Clean up side effects when the component is disconnected.
   */
  disconnectedCallback() {
    // Call and delete all cleanup functions
    if (this.cleanup) {
      this.cleanup.forEach((fn) => fn());
      this.cleanup.clear();
    }
  }

  /**
   * Set up the component markup.
   *
   * Note that this method may be called repeatedly and should be idempotent.
   */
  setup() {
    this.querySelectorAll("details").forEach((details) => {
      const menuItem = details.closest(".menu-item");

      replaceWith(
        details,
        createElement("ifrs-disclosure", {
          ...getAttributes(details),
          innerHTML: details.outerHTML,
          haspopup: true,
        }),
      );

      if (menuItem && this.contains(menuItem)) {
        menuItem.classList.add("menu-item-haspopup");
      }
    });
  }
}
